/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useSetAtom, useAtomValue } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { SendTestBroadcastModalRenderer } from '../../../shared/modules/demo-account/v1/components';
import {
  HEADER_HEIGHT,
  IntegrationInfo,
  TEMPLATE_CREATION_RESTRICTION_ERROR_MODAL_STORAGE_KEYS,
  WabaNumberStatus,
} from '../../../shared/typings';
import { useShopifySignupDetailsAtom } from '../../shopify/v1/states';
import ChatListSection from './components/chat-list-section';
import LeadStagesFilters from './components/custom-list-filter';
import HomeMiddleware from './components/home-center/routes';
import BulkActionProgressPanel from './components/bulk-action-progress-panel';
import ChatFilters from './components/filters';
import {
  chatListAtom,
  customFieldConditionsAtom,
  selectedChatIdsListAtom,
  showFiltersAtom,
  toggleSelectedChatIdsAtom,
} from './states/chat-list';
import { RESET } from 'jotai/utils';
import useNotifications from '../../../shared/hooks/use-notifications';
import { useCustomNavigate } from '../../../shared/hooks/use-conversation-navigate';
import { ROUTES } from '../../../shared/constants/routes';
import shopifyStoreInstall from './requests/shopify-store-install';
import getUserDetails from './requests/get-user-details';
import { userProfileAtom } from '../../../shared/states/user';
import RestrictionErrorModal from '../../../shared/components/organisms/restriction-error-modal';
import { getWabaInfo } from '../../../shared/requests/get-waba-info';
import { isEmbedAtom } from '../../embed/state';
import { getChatSectionContainerStyles } from '../../embed/utils/get-chat-section-styles';
import useEmbedVisibilitySettings from '../../embed/hooks/use-embed-visibility-settings';
import useEmbedClient from '../../embed/hooks/use-embed-client';
import { EmbedClient } from '../../login/v1/typings';
import { getTeamMembersPrivateNotes } from './requests/get-team-members';
import useQuery from '../../../shared/hooks/useQuery';
import { TeamMembersPrivateNotes } from './typings';
import { teamMembersPrivateNotesAtom } from './states/team-members';

const HomePage = () => {
  const { currentCustomListId } = useAtomValue(chatListAtom);
  const [shopifySignupDetails, setShopifySignupDetails] = useShopifySignupDetailsAtom();
  const chatFilters = useAtomValue(showFiltersAtom);
  const setToggleSelectedChatIds = useSetAtom(toggleSelectedChatIdsAtom);
  const setSelectedChatIdsList = useSetAtom(selectedChatIdsListAtom);
  const setCustomFieldConditions = useSetAtom(customFieldConditionsAtom);
  const [disabledWabaDetails, setDisabledWabaDetails] = useState<IntegrationInfo | null>(null);

  const { addNotification } = useNotifications();
  const navigate = useCustomNavigate();
  const setProfileAtom = useSetAtom(userProfileAtom);
  const isEmbed = useAtomValue(isEmbedAtom);
  const embedClient = useEmbedClient();
  const { showChatFilters, showChatListPanel } = useEmbedVisibilitySettings();

  useQuery<TeamMembersPrivateNotes>({
    requestAtom: teamMembersPrivateNotesAtom,
    queryFunction: getTeamMembersPrivateNotes,
  });

  useEffect(() => {
    const handleShopifyStoreInstall = async () => {
      if (!shopifySignupDetails.shopId) {
        return;
      }

      try {
        await shopifyStoreInstall(shopifySignupDetails.shopId);
        addNotification({
          type: 'success',
          message: 'Shopify store connected successfully',
        });
        setShopifySignupDetails(RESET);
      } catch (error) {
        addNotification({
          type: 'error',
          message: (error as Error).message || 'Something went wrong',
        });
      } finally {
        navigate({
          pathname: `${ROUTES.SETTINGS}${ROUTES.SETTINGS_INTEGRATION}/shopify`,
        });
      }
    };

    const getUserProfile = async () => {
      try {
        const profile = await getUserDetails();
        setProfileAtom(profile);
      } catch (error) {
        addNotification({
          type: 'error',
          message: (error as Error).message || 'An error occurred. Please try again',
        });
      }
    };

    const getWabaInfoDetails = async () => {
      try {
        const wabaInfos = await getWabaInfo();
        const wabaDetails = wabaInfos.integrations.find(
          (info) => info.wabaInfo.wabaStatus === WabaNumberStatus.DISABLED
        );
        setDisabledWabaDetails(wabaDetails ?? null);
      } catch (error) {
        addNotification({
          type: 'error',
          message: (error as Error).message || 'An error occurred. Please try again',
        });
      }
    };

    getUserProfile();
    handleShopifyStoreInstall();
    getWabaInfoDetails();
  }, []);

  useEffect(() => {
    return () => {
      setSelectedChatIdsList([]);
      setToggleSelectedChatIds(false);
      setCustomFieldConditions({ type: 'clear' });
    };
  }, []);

  const chatSectionStyles = getChatSectionContainerStyles({
    isEmbed,
    chatFiltersOpen: chatFilters.open,
    hideChatFilters: !showChatFilters,
    hideChatListPanel: !showChatListPanel,
  });

  const showRibbon = useMemo(() => {
    return (
      !isEmbed ||
      ((embedClient === EmbedClient.SALESFORCE || embedClient === EmbedClient.SALESFORCEV2) && showChatListPanel)
    );
  }, [isEmbed, embedClient, showChatListPanel]);

  return (
    <div
      css={css`
        display: flex;
        height: 100%;
        width: 100%;
        overflow: hidden;
        flex-direction: column;
      `}
    >
      {showRibbon && <LeadStagesFilters />}
      <div
        css={css`
          height: ${showRibbon && !!currentCustomListId ? `calc(100% - ${HEADER_HEIGHT}px)` : '100%'};
          width: 100%;
          display: flex;
        `}
      >
        {!isEmbed || showChatListPanel ? (
          <>
            <ChatFilters />
            <ChatListSection />
          </>
        ) : null}
        <div
          css={[
            css`
              width: ${chatFilters.open ? 'calc(100% - 240px - 320px)' : 'calc(100% - 55px - 375px)'};
              min-width: ${chatFilters.open ? 'calc(100% - 240px - 320px)' : 'calc(100% - 55px - 375px)'};
              max-width: ${chatFilters.open ? 'calc(100% - 240px - 320px)' : 'calc(100% - 55px - 375px)'};
              transition: width 280ms ease-in-out, min-width 280ms ease-in-out, max-width 280ms ease-in-out;
              overflow: hidden;
            `,
            chatSectionStyles,
          ]}
        >
          <HomeMiddleware />
        </div>
        <SendTestBroadcastModalRenderer />
      </div>
      <RestrictionErrorModal
        open={
          !!disabledWabaDetails &&
          localStorage.getItem(TEMPLATE_CREATION_RESTRICTION_ERROR_MODAL_STORAGE_KEYS.SHOW_ACCOUNT_DISABLED_MODAL) !==
            'false'
        }
        businessId={disabledWabaDetails?.wabaInfo?.businessId ?? ''}
        onClose={() => {
          setDisabledWabaDetails(null);
          localStorage.setItem(
            TEMPLATE_CREATION_RESTRICTION_ERROR_MODAL_STORAGE_KEYS.SHOW_ACCOUNT_DISABLED_MODAL,
            'false'
          );
        }}
        wabaId={disabledWabaDetails?.wabaInfo?.wabaId ?? ''}
        wabaName={disabledWabaDetails?.integrationName ?? 'WABA'}
      />
      <BulkActionProgressPanel />
    </div>
  );
};

export default HomePage;
